import { sentry } from '$shared/helpers/logger/sentry';
import { OtherExpection } from '$shared/exceptions/OtherExpection';
import ApiError from '$shared/models/api/ApiError';

const SuppportedFileContentTypes = ['application/octet-stream', 'text/csv', 'application/pdf'];

const uploadFile = (
  baseUrl: string,
  method: string,
  headers: any,
  data: FormData,
  uploadCallback: (
    completed: number,
    total: number,
    requestFinished: boolean,
    error: ApiError
  ) => void
): void => {
  if (!headers) {
    headers = {};
  }
  const xhr = new XMLHttpRequest();
  xhr.open(method, baseUrl);
  Object.keys(headers).forEach((key) => xhr.setRequestHeader(key, headers[key]));
  xhr.upload.addEventListener('progress', (e) => {
    if (e.lengthComputable) {
      uploadCallback(e.loaded, e.total, false, undefined);
    }
  });
  xhr.onreadystatechange = () => {
    try {
      if (xhr.readyState === 4) {
        if (xhr.status > 200 && xhr.status < 300) {
          uploadCallback(0, 0, true, undefined);
          return;
        } else {
          const error = ApiError.fromXhr(xhr);
          uploadCallback(0, 0, true, error);
        }
      }
    } catch (err) {
      const exception = OtherExpection.from('API Executor', 'uploadFile', err, {
        baseUrl,
        method,
        headers,
        data
      });
      sentry.log(exception);
      uploadCallback(0, 0, false, e);
    }
  };
  xhr.send(data);
};

const processResponse = (response: Response): Promise<any> => {
  if (response.status === 200) {
    return SuppportedFileContentTypes.includes(response.headers.get('Content-Type'))
      ? response.blob()
      : response.json().catch(console.log);
  }
  if (response.status === 201) {
    return response?.json();
  }
};

const execute = async (
  baseUrl: string,
  method: string,
  headers: any = {},
  data: unknown | FormData | undefined,
  uploadCallback: (completed: number) => void
): Promise<any> => {
  if (data instanceof FormData && uploadCallback) {
    uploadFile(baseUrl, method, headers, data, uploadCallback);
  } else {
    const requestHeaders = new Headers(headers);
    let body = null;
    if (data instanceof FormData) {
      body = data;
    } else {
      body = data ? JSON.stringify(data) : null;
      body && requestHeaders.append('Content-Type', 'application/json');
    }

    const response = await fetch(baseUrl, {
      method,
      headers: requestHeaders,
      body
    });
    if (!response.ok) {
      const apiError = await ApiError.fromResponse(response);
      throw apiError;
    }
    return processResponse(response);
  }
};

export default execute;
